import Env from '../../fw/env';
import FW from '../../fw/fw';
import Logger from '../../fw/logger';

export default class ConfigureHlsJS {

  #rmp;
  #enableCaptions = true;

  constructor(rmp) {
    this.#rmp = rmp;
  }

  get enableCaptions() {
    return this.#enableCaptions;
  }

  reset() {
    this.#enableCaptions = true;
  }

  prepareConfig() {
    let debug = false;
    if (DEBUG) {
      if (this.#rmp.debugLevel === 2) {
        debug = true;
      }
    }
    if (this.#rmp.hasExternalCCFiles) {
      this.#enableCaptions = false;
    } else {
      this.#enableCaptions = true;
    }

    const retryParameters = this.#rmp.retryParameters;
    const manifestLoadPolicy = {
      default: {
        maxTimeToFirstByteMs: Infinity,
        maxLoadTimeMs: retryParameters.manifest.timeout,
        timeoutRetry: {
          maxNumRetry: retryParameters.manifest.maxAttempts - 1,
          retryDelayMs: 0,
          maxRetryDelayMs: 0,
        },
        errorRetry: {
          maxNumRetry: retryParameters.manifest.maxAttempts - 2,
          retryDelayMs: retryParameters.manifest.delay,
          maxRetryDelayMs: 8000,
        }
      }
    };
    const playlistLoadPolicy = {
      default: {
        maxTimeToFirstByteMs: retryParameters.levels.timeout,
        maxLoadTimeMs: retryParameters.levels.timeout,
        timeoutRetry: {
          maxNumRetry: retryParameters.levels.maxAttempts - 1,
          retryDelayMs: 0,
          maxRetryDelayMs: 0,
        },
        errorRetry: {
          maxNumRetry: retryParameters.levels.maxAttempts - 1,
          retryDelayMs: retryParameters.levels.delay,
          maxRetryDelayMs: 8000,
        },
      },
    };
    const fragLoadPolicy = {
      default: {
        maxTimeToFirstByteMs: retryParameters.segment.timeout,
        maxLoadTimeMs: retryParameters.segment.timeout,
        timeoutRetry: {
          maxNumRetry: retryParameters.segment.maxAttempts - 1,
          retryDelayMs: 0,
          maxRetryDelayMs: 0,
        },
        errorRetry: {
          maxNumRetry: retryParameters.segment.maxAttempts + 1,
          retryDelayMs: retryParameters.segment.delay,
          maxRetryDelayMs: 8000,
        },
      },
    };
    const steeringManifestLoadPolicy = {
      default: {
        maxTimeToFirstByteMs: retryParameters.steering.timeout,
        maxLoadTimeMs: retryParameters.steering.timeout,
        timeoutRetry: {
          maxNumRetry: retryParameters.steering.maxAttempts - 1,
          retryDelayMs: 0,
          maxRetryDelayMs: 0,
        },
        errorRetry: {
          maxNumRetry: retryParameters.steering.maxAttempts - 2,
          retryDelayMs: retryParameters.steering.delay,
          maxRetryDelayMs: 8000,
        },
      }
    };
    const interstitialAssetListLoadPolicy = {
      default: {
        maxTimeToFirstByteMs: retryParameters.interstitial.timeout,
        maxLoadTimeMs: retryParameters.interstitial.timeout,
        timeoutRetry: {
          maxNumRetry: retryParameters.interstitial.maxAttempts - 1,
          retryDelayMs: 0,
          maxRetryDelayMs: 0
        },
        errorRetry: {
          maxNumRetry: retryParameters.interstitial.maxAttempts - 2,
          retryDelayMs: retryParameters.interstitial.delay,
          maxRetryDelayMs: 8000
        }
      }
    };
    const config = {
      // debug
      debug,
      // cap
      capLevelToPlayerSize: this.#rmp.capLevelToPlayerSize,
      ignoreDevicePixelRatio: this.#rmp.ignoreDevicePixelRatio,
      abrMaxWithRealBitrate: true,
      // MediaCapabilies 
      useMediaCapabilities: true,
      // MMS
      preferManagedMediaSource: true,
      // buffer
      maxMaxBufferLength: this.#rmp.hlsJSMaxBufferAhead,
      maxBufferSize: 512 * 1000 * 1000, // 512 MB
      backBufferLength: this.#rmp.hlsJSMaxBufferBehind,
      // captions 
      enableCEA708Captions: this.#enableCaptions,
      enableWebVTT: this.#enableCaptions,
      enableIMSC1: this.#enableCaptions,
      renderTextTracksNatively: this.#enableCaptions,
      captionsTextTrack1Label: this.#rmp.hlsJSCaptionsTextTrack1Label,
      captionsTextTrack1LanguageCode: 'cc1',
      captionsTextTrack2Label: this.#rmp.hlsJSCaptionsTextTrack2Label,
      captionsTextTrack2LanguageCode: 'cc2',
      captionsTextTrack3Label: this.#rmp.hlsJSCaptionsTextTrack3Label,
      captionsTextTrack3LanguageCode: 'cc3',
      captionsTextTrack4Label: this.#rmp.hlsJSCaptionsTextTrack4Label,
      captionsTextTrack4LanguageCode: 'cc4',
      // Metadata
      enableDateRangeMetadataCues: true,
      enableEmsgMetadataCues: true,
      enableID3MetadataCues: true,
      enableEmsgKLVMetadata: true,
      // LoadPolicy 
      manifestLoadPolicy,
      playlistLoadPolicy,
      fragLoadPolicy,
      steeringManifestLoadPolicy,
      interstitialAssetListLoadPolicy
    };
    if (this.#rmp.hlsJSFetchXhrWithCredentials) {
      Logger.print('hls.js', 'using withCredentials for Fetch and XHR requests');
      config.xhrSetup = (xhr) => {
        xhr.withCredentials = true; // do send cookies
      };
      config.fetchSetup = (context, initParams) => {
        // Always send cookies, even for cross-origin calls.
        initParams.credentials = 'include';
        return new Request(context.url, initParams);
      };
    }
    if (this.#rmp.hlsJSAppleAppStoreCompliance) {
      config.minAutoBitrate = 192001;
    }

    const bwEstimate = Env.getEstimateBW(true);
    if (bwEstimate > 0) {
      config.testBandwidth = false;
      config.abrEwmaDefaultEstimate = config.abrEwmaDefaultEstimateMax = bwEstimate;
    }

    if (this.#rmp.enableCMCD) {
      config.cmcd = {
        contentId: this.#rmp.cmcdContentId || FW.getCMCDContentId(this.#rmp.contentMetadata.id),
        sessionId: this.#rmp.cmcdSessionId || FW.generateUuid(),
        useHeaders: this.#rmp.useHeadersForCMCD
      };
      if (this.#rmp.cmcdIncludeKeys[0]) {
        config.cmcd.includeKeys = this.#rmp.cmcdIncludeKeys;
      }
    }
    // audio/subtitle preferences
    if (typeof this.#rmp.audioPreference.lang === 'string' && this.#rmp.audioPreference.lang !== '') {
      config.audioPreference.lang = this.#rmp.audioPreference.lang;
    }
    if (typeof this.#rmp.subtitlePreference.lang === 'string' && this.#rmp.subtitlePreference.lang !== '') {
      config.subtitlePreference.lang = this.#rmp.subtitlePreference.lang;
    }
    if (this.#rmp.hlsJSProgressive) {
      config.progressive = true;
    }
    if (this.#rmp.lowLatencyMode) {
      config.lowLatencyMode = true;
    }
    if (typeof this.#rmp.videoPreference.videoHdr === 'string' && this.#rmp.videoPreference.videoHdr !== 'auto') {
      this.#rmp.videoPreference.videoHdr = this.#rmp.videoPreference.videoHdr.toUpperCase();
      if (['SDR', 'PQ', 'HLG'].includes(this.#rmp.videoPreference.videoHdr)) {
        config.videoPreference = {
          preferHDR: true,
          allowedVideoRanges: [this.#rmp.videoPreference.videoHdr]
        };
      }
    }
    if (!FW.isEmptyObject(this.#rmp.hlsJSCustomConfig)) {
      const keys = Object.keys(this.#rmp.hlsJSCustomConfig);
      keys.forEach(key => {
        config[key] = this.#rmp.hlsJSCustomConfig[key];
      });
      Logger.print('hls.js', 'custom config being passed follows', config);
    }
    return config;
  }

}
